import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { ChevronLeft, ChevronRight, Search, CheckCircle, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useToaster } from "../../../contexts/ToasterContext";
import { ENV } from "../../../utils/env.js";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "../../../components/ui/breadcrumb";

// Shimmer Loading Component
const ShimmerCard = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
);

const ShimmerText = ({ width = "w-3/4", height = "h-4" }) => (
  <ShimmerCard className={`${width} ${height} mb-2`} />
);

const ChannelShimmer = () => (
  <div className="flex items-center gap-3 p-4">
    <ShimmerCard className="w-4 h-4 rounded" />
    <div className="flex-1 min-w-0">
      <ShimmerText width="w-3/4" height="h-4" />
      <ShimmerText width="w-1/2" height="h-3" />
    </div>
  </div>
);

const TeamsChannelOnboardingPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToaster();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1 - Channel Selection
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingChannels, setIsLoadingChannels] = useState(true);
  
  // Normal mode pagination
  const [normalPage, setNormalPage] = useState(1);
  const [normalPerPage, setNormalPerPage] = useState(50);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    last_page: 1,
    has_next: false,
    has_previous: false
  });
  
  // Search mode pagination
  const [searchPage, setSearchPage] = useState(1);
  const [searchPerPage, setSearchPerPage] = useState(30);
  const [searchPagination, setSearchPagination] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Step 2 - Monitoring Configuration
  const [alertFrequency, setAlertFrequency] = useState('immediate');
  const [detectorTypes, setDetectorTypes] = useState(['AWS', 'GitHub', 'Slack']);
  const [escalationLevel, setEscalationLevel] = useState('medium');
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);
  const [isCreatingMonitoring, setIsCreatingMonitoring] = useState(false);
  const [showSummaryAccordion, setShowSummaryAccordion] = useState(false);

  // Fetch available Teams channels from Teams API
  const fetchChannels = async (page = normalPage, per_page = normalPerPage) => {
    setIsLoadingChannels(true);
    try {
      const token = localStorage.getItem("jwt");
      const response = await fetch(
        `${ENV.REACT_APP_URL}api/teams/channels/non-onboarded`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pagination: {
              page: page,
              per_page: per_page
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Teams channels");
      }

      const data = await response.json();
      const items = Array.isArray(data.data) ? data.data : [];
      // Client-side guard: enforce current per-page in case backend returns extra
      const limitedItems = items.slice(0, per_page);
      setChannels(limitedItems);
      
      const paginationData = data.pagination || {};
      setPagination({
        page: paginationData.page || page || 1,
        per_page: per_page,
        last_page: Math.max(1, Math.ceil((paginationData.total_count || limitedItems.length) / per_page)),
        has_next: Boolean(paginationData.has_next && (Math.ceil((paginationData.total_count || 0) / per_page) > (paginationData.page || page || 1))),
        has_previous: Boolean(paginationData.has_previous || (paginationData.page || page || 1) > 1),
      });
    } catch (error) {
      console.error("Error fetching Teams channels:", error);
      showToast("Failed to fetch Teams channels", "error");
      setChannels([]);
    } finally {
      setIsLoadingChannels(false);
    }
  };

  // Search Teams channels
  const searchTeamsChannels = async (query, page = 1, per_page = 30) => {
    if (!query || query.length < 2) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchPagination(null);
      return;
    }

    setIsSearchLoading(true);
    try {
      const token = localStorage.getItem("jwt");
      const response = await fetch(
        `${ENV.REACT_APP_URL}api/teams/search/channels?q=${encodeURIComponent(query)}&page=${page}&per_page=${per_page}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to search Teams channels");
      }

      const data = await response.json();
      const items = Array.isArray(data.data) ? data.data : [];
      // Client-side guard for per-page enforcement
      const limitedItems = items.slice(0, per_page);
      setSearchResults(limitedItems);
      
      const paginationData = data.pagination || {};
      setSearchPagination({
        page: paginationData.page || page || 1,
        per_page: per_page,
        last_page: Math.max(1, Math.ceil((paginationData.total_count || limitedItems.length) / per_page)),
        has_next: Boolean(paginationData.has_next && (Math.ceil((paginationData.total_count || 0) / per_page) > (paginationData.page || page || 1))),
        has_previous: Boolean(paginationData.has_previous || (paginationData.page || page || 1) > 1),
      });
      setIsSearchMode(true);
    } catch (error) {
      console.error("Error searching Teams channels:", error);
      showToast("Failed to search Teams channels", "error");
      setSearchResults([]);
    } finally {
      setIsSearchLoading(false);
    }
  };

  // Initialize channels on component mount
  useEffect(() => {
    fetchChannels();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get the channels to display (search results or normal channels)
  const displayedChannels = useMemo(() => {
    const channelsToDisplay = isSearchMode ? searchResults : channels;
    
    // Sort channels to move selected ones to the top
    return [...channelsToDisplay].sort((a, b) => {
      const aSelected = selectedChannels.some((channel) => channel.channel_id === a.channel_id);
      const bSelected = selectedChannels.some((channel) => channel.channel_id === b.channel_id);
      
      // If one is selected and the other isn't, selected comes first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      
      // If both are selected or both are unselected, maintain original order
      return 0;
    });
  }, [isSearchMode, searchResults, channels, selectedChannels]);

  // Handle search query changes with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchTeamsChannels(searchQuery, 1, searchPerPage);
      } else if (searchQuery.trim().length === 0) {
        setIsSearchMode(false);
        setSearchResults([]);
        setSearchPagination(null);
        // Reset to first page of normal results when clearing search
        if (normalPage !== 1) {
          setNormalPage(1);
          fetchChannels(1, normalPerPage);
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Channel selection handlers
  const handleSelectChannel = (channel) => {
    // Preserve scroll position of the list container
    const listContainer = document.querySelector('.max-h-96.overflow-y-auto.border.rounded-md');
    const scrollTop = listContainer?.scrollTop || 0;

    setSelectedChannels((prev) => {
      const isSelected = prev.some((c) => c.channel_id === channel.channel_id);
      if (isSelected) {
        return prev.filter((c) => c.channel_id !== channel.channel_id);
      } else {
        return [...prev, channel];
      }
    });

    // Restore scroll position after state update and re-render
    setTimeout(() => {
      if (listContainer) {
        listContainer.scrollTop = scrollTop;
      }
    }, 0);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showConfigDropdown && !event.target.closest('.config-dropdown')) {
        setShowConfigDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showConfigDropdown]);

  // Navigation handlers
  const handleNext = () => {
    if (currentStep === 1 && selectedChannels.length === 0) {
      showToast("Please select at least one channel", "error");
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/integration/teams');
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Create channel monitoring function
  const createChannelMonitoring = async (monitoringData) => {
    try {
      const token = localStorage.getItem("jwt");
      const response = await fetch(`${ENV.REACT_APP_URL}api/teams/monitoring/schedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(monitoringData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create channel monitoring");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating channel monitoring:", error);
      throw error;
    }
  };

  // Add channels to database first
  const addChannelsToDatabase = async (channels) => {
    try {
      const token = localStorage.getItem("jwt");
      const response = await fetch(`${ENV.REACT_APP_URL}api/teams/channels`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ channels }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add channels");
      }

      return await response.json();
    } catch (error) {
      console.error("Error adding channels:", error);
      throw error;
    }
  };

  // Final submission handler
  const handleCreateMonitoring = async () => {
    setIsCreatingMonitoring(true);
    
    try {
      // Step 1: Add channels to database
      console.log('Step 1: Adding channels to database...', selectedChannels);
      await addChannelsToDatabase(selectedChannels);
      showToast('Channels added successfully!', 'success');
      
      // Store channel info for background monitoring setup
      const channelInfo = {
        selectedChannels: [...selectedChannels],
        alertFrequency,
        detectorTypes,
        escalationLevel
      };
      
      // Navigate to main Teams page immediately
      navigate('/integration/teams');
      
      // Run monitoring setup in background
      setupMonitoringInBackground(channelInfo);
      
    } catch (error) {
      console.error("Error in handleCreateMonitoring:", error);
      
      // Handle different types of errors
      if (error?.message) {
        showToast(error.message, "error");
      } else {
        showToast("Failed to add channels", "error");
      }
    } finally {
      setIsCreatingMonitoring(false);
    }
  };

  // Background monitoring setup function
  const setupMonitoringInBackground = async (channelInfo) => {
    try {
      // Wait for channels to be processed
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Step 2: Fetch managed channels to get their internal IDs
      console.log('Step 2: Fetching managed channels to get internal IDs...');
      const token = localStorage.getItem("jwt");
      const managedChannelsResponse = await fetch(`${ENV.REACT_APP_URL}api/teams/channels/managed`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!managedChannelsResponse.ok) {
        throw new Error("Failed to fetch managed channels");
      }

      const managedData = await managedChannelsResponse.json();
      const managedChannels = managedData.data || managedData.channels || managedData || [];
      console.log('Managed channels response:', managedChannels);
      
      // Step 3: Map selected channels to their internal IDs
      const channelMapping = {};
      const validChannelIds = [];
      const invalidChannels = [];
      
      channelInfo.selectedChannels.forEach(selectedChannel => {
        const managedChannel = managedChannels.find(channel => 
          channel.channel_id === selectedChannel.channel_id
        );
        
        if (managedChannel && managedChannel.id) {
          const channelId = managedChannel.id;
          
          // Validate UUID format
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (uuidRegex.test(channelId)) {
            channelMapping[selectedChannel.channel_id] = channelId;
            validChannelIds.push(channelId);
            console.log(`Mapped ${selectedChannel.channel_name} to ID: ${channelId}`);
          } else {
            console.error('Invalid UUID format for channel:', selectedChannel.channel_name, 'ID:', channelId);
            invalidChannels.push({ channelName: selectedChannel.channel_name, error: 'Invalid channel ID format' });
          }
        } else {
          console.error('No managed channel found for:', selectedChannel.channel_name);
          invalidChannels.push({ channelName: selectedChannel.channel_name, error: 'Channel not found in database' });
        }
      });
      
      if (validChannelIds.length === 0) {
        showToast('No valid channels found for monitoring setup', 'error');
        return;
      }
      
      // Step 4: Create monitoring configuration using internal channel IDs
      console.log('Step 4: Creating monitoring for channels:', validChannelIds);
      
      const monitoringData = {
        alertFrequency: channelInfo.alertFrequency,
        detectorTypes: channelInfo.detectorTypes,
        escalationLevel: channelInfo.escalationLevel,
        channelIds: validChannelIds
      };
      
      console.log('Monitoring data:', monitoringData);
      
      const monitoringResponse = await createChannelMonitoring(monitoringData);
      console.log('Monitoring setup successful:', monitoringResponse);
      
      showToast(
        `Successfully configured monitoring for ${validChannelIds.length} channel${validChannelIds.length > 1 ? 's' : ''}!`,
        "success"
      );
      
      if (invalidChannels.length > 0) {
        const errorMessage = `Failed to configure ${invalidChannels.length} channel(s): ${invalidChannels.map(f => f.channelName).join(', ')}`;
        showToast(errorMessage, 'warning');
      }
      
    } catch (error) {
      console.error("Background monitoring setup error:", error);
      showToast('Failed to configure channel monitoring. Please try again from channel settings.', 'error');
    }
  };

  // Pagination handlers for normal mode
  const handlePrevPage = () => {
    if (pagination.has_previous) {
      const newPage = normalPage - 1;
      setNormalPage(newPage);
      fetchChannels(newPage, normalPerPage);
    }
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      const newPage = normalPage + 1;
      setNormalPage(newPage);
      fetchChannels(newPage, normalPerPage);
    }
  };

  const handlePerPageChange = (perPage) => {
    const perPageNum = Number(perPage);
    setNormalPerPage(perPageNum);
    setNormalPage(1);
    fetchChannels(1, perPageNum);
  };

  // Search pagination handlers
  const handleSearchPrevPage = () => {
    if (searchPagination?.has_previous) {
      const newPage = searchPage - 1;
      setSearchPage(newPage);
      searchTeamsChannels(searchQuery, newPage, searchPerPage);
    }
  };

  const handleSearchNextPage = () => {
    if (searchPagination?.has_next) {
      const newPage = searchPage + 1;
      setSearchPage(newPage);
      searchTeamsChannels(searchQuery, newPage, searchPerPage);
    }
  };

  const handleSearchPerPageChange = (perPage) => {
    const perPageNum = Number(perPage);
    setSearchPerPage(perPageNum);
    setSearchPage(1);
    searchTeamsChannels(searchQuery, 1, perPageNum);
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search channels..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Counter */}
            <div className="flex items-center justify-end pb-2 border-b">
              {selectedChannels.length > 0 && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {selectedChannels.length} channel{selectedChannels.length > 1 ? "s" : ""} selected
                </span>
              )}
            </div>

            {/* Channels List */}
            <div className="max-h-96 overflow-y-auto border rounded-md">
              {isLoadingChannels || isSearchLoading ? (
                <div className="divide-y">
                  {[...Array(8)].map((_, index) => (
                    <ChannelShimmer key={index} />
                  ))}
                </div>
              ) : displayedChannels.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  <p className="text-center">
                    {searchQuery.trim().length >= 2 ? (
                      'No channels found matching your search.'
                    ) : searchQuery.trim() ? (
                      'Type at least 2 characters to search'
                    ) : (
                      'No channels available for onboarding.'
                    )}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {displayedChannels.map((channel) => {
                    const isSelected = selectedChannels.some((c) => c.channel_id === channel.channel_id);
                    return (
                      <label
                        key={channel.channel_id}
                        className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-purple-50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectChannel(channel)}
                          className="h-4 w-4 text-[#44087D] border-gray-300 rounded focus:ring-[#44087D] focus:ring-2"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {channel.channel_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate mt-1">
                            Team: {channel.team_name}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 sm:gap-0 pt-4 border-t">
              {isSearchMode ? (
                // Search mode pagination
                <>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSearchPrevPage}
                      disabled={!searchPagination?.has_previous}
                      className="flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600 mx-2">
                      Page {searchPagination?.page || 1} of {searchPagination?.last_page || 1}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSearchNextPage}
                      disabled={!searchPagination?.has_next}
                      className="flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Per page:</label>
                    <select
                      value={searchPerPage}
                      onChange={(e) => handleSearchPerPageChange(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                    >
                      {[5, 10, 20, 30, 50].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                // Normal mode pagination
                <>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={!pagination.has_previous}
                      className="flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600 mx-2">
                      Page {pagination.page} of {pagination.last_page}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={!pagination.has_next}
                      className="flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Per page:</label>
                    <select
                      value={pagination.per_page}
                      onChange={(e) => handlePerPageChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
                    >
                      {[5, 10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-sm text-gray-600 mb-4">
              Configure monitoring settings for {selectedChannels.length} channel{selectedChannels.length > 1 ? 's' : ''}.
            </div>

            {/* Collapsible Summary Accordion */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100"
                onClick={() => setShowSummaryAccordion(!showSummaryAccordion)}
              >
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Selected Channels - Summary
                  </h3>
                  <span className="text-sm text-gray-500">
                    ({selectedChannels.length} channel{selectedChannels.length > 1 ? 's' : ''})
                  </span>
                </div>
                {showSummaryAccordion ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </div>
              
              {showSummaryAccordion && (
                <div className="border-t p-4 bg-white">
                  {isCreatingMonitoring ? (
                    <div className="space-y-3">
                      {[...Array(selectedChannels.length)].map((_, index) => (
                        <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <ShimmerCard className="w-4 h-4 rounded" />
                            <div className="flex-1">
                              <ShimmerText width="w-1/2" height="h-4" />
                              <ShimmerText width="w-1/4" height="h-3" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      <div className="flex flex-wrap gap-2">
                        {selectedChannels.map((channel) => (
                          <span
                            key={channel.channel_id}
                            title={`Team: ${channel.team_name}`}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                          >
                            {channel.channel_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Monitoring Configuration Panel */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Monitoring Configuration</h3>
              </div>

              {/* Alert Frequency Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Alert Frequency</label>
                <div className="flex gap-2">
                  <label className="flex items-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 flex-1">
                    <input
                      type="radio"
                      name="alertFrequency"
                      value="immediate"
                      checked={alertFrequency === 'immediate'}
                      onChange={(e) => setAlertFrequency(e.target.value)}
                      className="w-4 h-4 text-blue-600 mr-2"
                    />
                    <div>
                      <div className="text-sm font-medium">Immediate</div>
                      <div className="text-xs text-gray-500">Alert on every detection</div>
                    </div>
                  </label>
                  <label className="flex items-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 flex-1">
                    <input
                      type="radio"
                      name="alertFrequency"
                      value="hourly"
                      checked={alertFrequency === 'hourly'}
                      onChange={(e) => setAlertFrequency(e.target.value)}
                      className="w-4 h-4 text-blue-600 mr-2"
                    />
                    <div>
                      <div className="text-sm font-medium">Hourly Summary</div>
                      <div className="text-xs text-gray-500">Batch alerts every hour</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Detector Types */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Detector Types</label>
                <div className="flex flex-wrap gap-2">
                  {['AWS', 'GitHub', 'Slack', 'Azure', 'GCP', 'Docker'].map((detector) => (
                    <label key={detector} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={detectorTypes.includes(detector)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setDetectorTypes([...detectorTypes, detector]);
                          } else {
                            setDetectorTypes(detectorTypes.filter(d => d !== detector));
                          }
                        }}
                        className="w-4 h-4 text-blue-600 mr-2"
                      />
                      <span className="text-sm text-gray-700">{detector}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Escalation Level */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Escalation Level</label>
                <select
                  value={escalationLevel}
                  onChange={(e) => setEscalationLevel(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="low">Low - Email notifications only</option>
                  <option value="medium">Medium - Email + Teams alerts</option>
                  <option value="high">High - Email + Teams + Slack alerts</option>
                  <option value="critical">Critical - All channels + SMS</option>
                </select>
              </div>

              {/* Monitoring Preview */}
              <div className="bg-white border border-purple-200 rounded-md p-3">
                <div className="text-sm text-purple-800">
                  <strong>Preview:</strong> {alertFrequency === 'immediate' ? 'Immediate' : 'Hourly'} alerts for {detectorTypes.join(', ')} detectors with {escalationLevel} escalation level
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        {/* Breadcrumb + Page Title */}
        <div className="mb-8 space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/integration/teams">Teams</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Onboard Channels</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <h1 className="text-black text-2xl md:text-3xl font-bold">Onboard Channels</h1>
        </div>

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep >= step
                    ? 'bg-[#44087D] border-[#44087D] text-white'
                    : 'bg-white border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-medium">{step}</span>
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  currentStep >= step ? 'text-[#44087D]' : 'text-gray-500'
                }`}>
                  {step === 1 && 'Select Channels'}
                  {step === 2 && 'Configure Monitoring'}
                </span>
                {step < 2 && (
                  <div className={`w-8 h-0.5 mx-4 ${
                    currentStep > step ? 'bg-[#44087D]' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card className="bg-white border border-gray-300 shadow-none">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900">
              {currentStep === 1 && 'Select Channels to Onboard'}
              {currentStep === 2 && 'Configure Monitoring Settings'}
            </CardTitle>
          </CardHeader>
          <CardContent>{renderStepContent()}</CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>

          <div className="flex gap-2">
            {currentStep < 2 ? (
              <Button
                onClick={handleNext}
                disabled={currentStep === 1 && selectedChannels.length === 0}
                className="bg-[#44087D] hover:bg-[#5a0a9a] text-white flex items-center gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleCreateMonitoring}
                disabled={isCreatingMonitoring}
                className="bg-[#44087D] hover:bg-[#5a0a9a] text-white flex items-center gap-2"
              >
                {isCreatingMonitoring ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Setting up Monitoring...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Setup Monitoring
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamsChannelOnboardingPage;